import { useState } from "react";
import {
  accountService,
  homeService,
  subscriptionService,
} from "@miplatform/api";
import {
  HTTP_STATUSES,
  PRINT_ORIENTATION,
  TIME_UNITS,
} from "@miplatform/constants";
import { useComponentState, useDebounce, usePrint } from "@miplatform/hooks";
import {
  IDateRangeISO,
  IQuickBooksAuditListResponse,
  QuickBookSyncStatus,
} from "@miplatform/types";
import {
  buildSearchQuery,
  endOf,
  getDateDay,
  getDateISO,
  startOf,
} from "@miplatform/utils";
import { useSession } from "@mibuildweb/hooks";
import { Filters, QUICKBOOKS_TYPES_ALL, optionsType } from "./constants";

export const useAuditLogViewModel = () => {
  const {
    session: { tenantId },
  } = useSession();
  const stateManager = useComponentState();
  const isLoading = stateManager.isBusy();
  const [auditLogs, setAuditLogs] = useState<IQuickBooksAuditListResponse[]>(
    []
  );
  const [customers, setCustomers] = useState<optionsType[]>([]);
  const [communities, setCommunities] = useState<optionsType[]>([]);
  const [account, setAccount] = useState<any>(null);
  const { handlePrintCustomJsx } = usePrint(account);

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);

  const [dateRange, setDateRange] = useState<IDateRangeISO>({
    startDate: getDateISO(getDateDay()),
    endDate: null,
  });

  const [filters, setFilters] = useState<Filters>({
    sortBy: "lastSyncDate",
    status: "All",
    accountId: "",
    communityId: "",
    quickBooksType: "All",
  });

  const handleSearch = (value: string) => {
    setSearch(value);
  };

  const onDateChange = (newDateRange: IDateRangeISO) => {
    setDateRange(newDateRange);
  };

  const searchQuery = buildSearchQuery(debouncedSearch, ["searchField"]);

  const commonFilters = ({ top, skip }: { top: number; skip: number }) => {
    // If there is a debouncedSearch, only send searchQuery as filters
    if ((search || debouncedSearch) && searchQuery) {
      return {
        sort: [`${filters.sortBy} desc`],
        top: 25,
        skip,
        params: {},
        signal: null,
        toastMessageConfig: {},
        filters: { ...searchQuery },
      };
    }

    // Otherwise, send all filters except searchQuery
    return {
      sort: [`${filters.sortBy} desc`],
      top: 25,
      skip,
      params: {},
      signal: null,
      toastMessageConfig: {},
      filters: {
        ...(filters.status && {
          status: {
            in:
              filters.status === "All"
                ? [
                    QuickBookSyncStatus.Synced,
                    QuickBookSyncStatus.NotSynced,
                    QuickBookSyncStatus.Failed,
                    QuickBookSyncStatus.PartialSync,
                  ]
                : [filters.status],
          },
        }),
        ...(filters.accountId && {
          accountId: { type: "guid", value: filters.accountId },
        }),
        ...(filters.communityId && {
          communityId: { type: "guid", value: filters.communityId },
        }),
        ...(filters.quickBooksType && {
          quickBooksType: {
            in:
              filters.quickBooksType === "All"
                ? QUICKBOOKS_TYPES_ALL
                : [filters.quickBooksType],
          },
        }),
        ...(dateRange && {
          lastSyncDate: {
            ge: startOf(TIME_UNITS.DAY, dateRange.startDate).toDate(),
            le: endOf(
              TIME_UNITS.DAY,
              dateRange.endDate ?? dateRange.startDate
            ).toDate(),
          },
        }),
      },
    };
  };

  const getAuditLogs = async ({ top, skip }: { top: number; skip: number }) => {
    return stateManager.run(async () =>
      accountService.quickBooksAudit.getAll(commonFilters({ top, skip }))
    );
  };

  const getAccount = async () => {
    await stateManager.run(async () => {
      const response = await subscriptionService.subscriptions.get(tenantId);

      if (response.status === HTTP_STATUSES.OK) {
        setAccount(response.data);
      }
    });
  };

  const syncObject = async (audit: IQuickBooksAuditListResponse) => {
    return stateManager.run(async () => {
      const { data, status } = await accountService.quickBooksAudit.sync(
        audit,
        {
          success: { text: "Sync was successful." },
          error: { text: "Sync failed. Please try again later." },
        }
      );

      if (status === HTTP_STATUSES.OK) {
        setAuditLogs((prevAudits) =>
          prevAudits.map((item) =>
            item.id === data.id ? { ...item, ...data } : item
          )
        );
        return true;
      }
      return false;
    });
  };

  const getCustomers = async () => {
    return stateManager.run(async () => {
      const { status, data } = await accountService.accounts.getAll({
        sort: "name",
        filters: {
          accountType: {
            in: ["Builder", "Service"],
          },
        },
      });

      if (status === HTTP_STATUSES.OK) {
        setCustomers(
          data.map((customer: { name: string; id: string }) => {
            return {
              label: customer.name,
              value: customer.id,
            };
          }) || []
        );
      }
      return [];
    });
  };

  const getCommunities = async () => {
    await stateManager.run(async () => {
      const { status, data } = await homeService.communities.getAll({
        sort: "name",
      });

      if (status === HTTP_STATUSES.OK) {
        setCommunities(
          data.map((community: { name: string; id: string }) => {
            return {
              label: community.name,
              value: community.id,
            };
          }) || []
        );
      }
    });
  };

  const handlePrintAudits = (jsx: React.ReactNode | null) => {
    handlePrintCustomJsx(jsx, {
      orientation: PRINT_ORIENTATION.LANDSCAPE,
    });
  };

  return {
    dateRange,
    setDateRange,
    getAuditLogs,
    onDateChange,
    handleSearch,
    getCommunities,
    getCustomers,
    auditLogs,
    customers,
    communities,
    search,
    isLoading,
    filters,
    debouncedSearch,
    setFilters,
    setAuditLogs,
    getAccount,
    handlePrintAudits,
    syncObject,
  };
};
